import { Button } from "@progress/kendo-react-buttons";
import { downloadIcon } from "@progress/kendo-svg-icons";
import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useState } from "react";
import { useListScenariosQuery, useSaveScenarioMutation } from "../../../api/scenarioApi";
import { useAppDispatch } from "../../../app/hooks";
import { notify } from "../../../components/notify";
import type { ListScenariosRequest } from "../../../contracts";
import useCurrentPlan from "../../../hooks/useCurrentPlan";
import StateIndicator from "./atoms/stateIndicator";
import SaveScenarioWindow from "./saveScenarioWindow";
import SimulationHeading from "./simulationHeading";

interface SimulationToolbarProps {
    onExport: () => void;
    onShowSimulatorInput: () => void;
}

const SimulationToolbar: React.FC<SimulationToolbarProps> = ({
    onExport
}) => {
    const dispatch = useAppDispatch();
    const [saveScenario, { isLoading: saveScenarioIsLoading }] = useSaveScenarioMutation();
    const { chatConversationId, chatConversationMessageId, headingData, currentPlan } = useCurrentPlan();
    const [saveScenarioWindowVisible, setSaveScenarioWindowVisible] = useState<boolean>(false);

    const listRequest: ListScenariosRequest | typeof skipToken = chatConversationId != null && chatConversationMessageId != null
        ? {
                take: 9999,
                filter: {
                    logic: "and",
                    filters: [
                        { field: "chatconversationid", operator: "eq", value: chatConversationId },
                        { field: "chatconversationmessageid", operator: "eq", value: chatConversationMessageId }
                    ]
                }
            }
        : skipToken;

    const {
        currentData: scenarioResponse,
        error: scenarioError,
        isFetching: scenarioIsFetching,
        refetch
    } = useListScenariosQuery(listRequest);

    const scenarios = scenarioResponse?.data;
    const scenarioSaved = scenarios != null && scenarios.length > 0;

    useEffect((): void => {
        if (!scenarioError) {
            return;
        }

        if ("status" in scenarioError && scenarioError.status === 404) {
            return;
        }

        dispatch(notify("An error occurred attempting to retrieve scenario.", "error"));
    }, [dispatch, scenarioError]);

    const buttonsDisabled = !currentPlan;
    const saveScenarioDisabled = buttonsDisabled
        || !chatConversationId
        || !chatConversationMessageId
        || scenarioIsFetching
        || saveScenarioIsLoading
        || scenarioSaved;

    const compareScenarioDisabled = buttonsDisabled
        || !chatConversationId
        || !chatConversationMessageId
        || scenarioIsFetching
        || saveScenarioIsLoading
        || scenarioSaved;

    const handleExportClick = (): void => {
        onExport();
    };

    const handleSaveScenarioClick = (): void => {
        setSaveScenarioWindowVisible(true);
    };

    const handleCompareScenarioClick = (): void => {
        // setSaveScenarioWindowVisible(true);
    };

    return (
        <>
            {saveScenarioWindowVisible && (
                <SaveScenarioWindow
                    scenarioDescription={[
                        headingData?.objective,
                        headingData?.skuName != null ? `for ${headingData.skuName}` : null,
                        headingData?.retailers ? `at ${headingData.retailers}` : null
                    ].filter(Boolean).join(" ")}
                    onClose={() => setSaveScenarioWindowVisible(false)}
                    onSave={async (name, description) => {
                        if (!chatConversationId || !chatConversationMessageId) {
                            return;
                        }

                        try {
                            await saveScenario({
                                name,
                                description,
                                chat_conversation_id: chatConversationId,
                                chat_conversation_message_id: chatConversationMessageId
                            }).unwrap();

                            setSaveScenarioWindowVisible(false);
                            await refetch();
                        } catch {
                            dispatch(notify("An error occurred attempting to save the scenario.", "error"));
                        }
                    }}
                />
            )}
            <div className="w-100 mb-1">
                <h1 className="fw-semibold simulator-title k-text-highlight-primary">Simulator</h1>
                <SimulationHeading />
                <div className="w-100 py-3 d-flex align-items-center justify-content-between gap-3">
                    <div className="d-flex align-items-center gap-3 flex-shrink-0">
                        <span
                            className="bg-success rounded-pill flex-shrink-0"
                            style={{ width: "4px", minHeight: "24px" }}
                            aria-hidden="true"
                        />

                        <h2 className="small fw-bold mb-0 text-nowrap">AI Generated Plan</h2>
                        <StateIndicator isSuccessful={scenarioSaved} successText="Saved" warningText="Unsaved Scenario" />
                    </div>

                    <div className="d-flex align-items-center justify-content-end gap-2 flex-wrap">
                        {/* <Button */}
                        {/*    type="button" */}
                        {/*    size="small" */}
                        {/*    fillMode="flat" */}
                        {/*    svgIcon={slidersIcon} */}
                        {/*    onClick={onShowSimulatorInput} */}
                        {/*    title="Simulation Inputs" */}
                        {/*    disabled={buttonsDisabled || !hasLatestToolMessage} */}
                        {/* /> */}

                        <Button
                            type="button"
                            fillMode="outline"
                            svgIcon={downloadIcon}
                            className="px-3"
                            onClick={handleExportClick}
                            disabled={buttonsDisabled}
                        >
                            Export
                        </Button>

                        {/* <Button */}
                        {/*    type="button" */}
                        {/*    fillMode="outline" */}
                        {/*    svgIcon={shareIcon} */}
                        {/*    className="px-3" */}
                        {/*    onClick={handleShareClick} */}
                        {/*    disabled={buttonsDisabled} */}
                        {/* > */}
                        {/*    Share */}
                        {/* </Button> */}

                        <Button
                            type="button"
                            themeColor="success"
                            fillMode="outline"
                            className="px-3 fw-semibold"
                            onClick={handleCompareScenarioClick}
                            disabled={compareScenarioDisabled}
                        >
                            Compare Scenario
                        </Button>

                        <Button
                            type="button"
                            themeColor="success"
                            fillMode="solid"
                            className="px-3 fw-semibold !k-bg-primary"
                            onClick={handleSaveScenarioClick}
                            disabled={saveScenarioDisabled}
                        >
                            Save Scenario
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SimulationToolbar;
